import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { getAuthToken } from "../../../utils/getAuthToken";

// API base URL with fallback
const SERVER_DOMAIN = process.env.REACT_APP_SERVER_DOMAIN || 'http://localhost:8000';

// Async thunk to add a role
export const addRole = createAsyncThunk(
  "addFunctions/addRole",
  async (roleData, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue({ message: "No token found for authentication" });
    }

    try {
      const response = await fetch(
        `${SERVER_DOMAIN}/api/roles/addrole`,
        {
          method: "POST",
          body: JSON.stringify(roleData),
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result = await response.json();

      if (!response.ok) {
        return rejectWithValue({
          message: result.message || "An error occurred while adding the role.",
        });
      }

      return { message: result.message || "Role added successfully" };
    } catch (error) {
      return rejectWithValue({
        message: error.message || "Failed to add role",
      });
    }
  }
);

// Async thunk to add a team member
export const addTeamMember = createAsyncThunk(
  "addFunctions/addTeamMember",
  async (memberData, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue({ message: "No token found for authentication" });
    }

    try {
      const response = await fetch(
        `${SERVER_DOMAIN}/api/teammembers/add`,
        {
          method: "POST",
          body: JSON.stringify(memberData),
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result = await response.json();

      if (!response.ok) {
        return rejectWithValue({
          message:
            result.message || "An error occurred while adding the team member.",
        });
      }

      return { message: result.message || "Team member added successfully" };
    } catch (error) {
      return rejectWithValue({
        message: error.message || "Failed to add team member",
      });
    }
  }
);

// Async thunk to create a new team
export const createTeam = createAsyncThunk(
  "addFunctions/createTeam",
  async (teamData, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue({ message: "No token found for authentication" });
    }

    try {
      const response = await fetch(
        `${SERVER_DOMAIN}/api/teams/add`,
        {
          method: "POST",
          body: JSON.stringify(teamData),
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      const result = await response.json();

      if (!response.ok) {
        return rejectWithValue({
          message:
            result.message || "An error occurred while creating the team.",
        });
      }

      return { message: result.message || "Team created successfully" };
    } catch (error) {
      return rejectWithValue({
        message: error.message || "Failed to create team",
      });
    }
  }
);

// Async thunk to delete a team member and return the updated team
export const removeTeamMember = createAsyncThunk(
  "addFunctions/removeTeamMember",
  async ({ teamId, memberId }, { rejectWithValue }) => {
    // Expecting both teamId and memberId
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue({ message: "No token found for authentication" });
    }

    try {
      const response = await fetch(
        `${SERVER_DOMAIN}/api/teams/deletemember/${teamId}`, // teamId in the URL
        {
          method: "DELETE",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            memberId: memberId, // memberId in the body
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        return rejectWithValue({
          message:
            result.message ||
            "An error occurred while deleting the team member.",
        });
      }

      // Return the updated team along with a success message
      return {
        message: result.message || "Team member deleted successfully",
        team: result.team || {}, // Assuming 'result.team' contains the updated team data
      };
    } catch (error) {
      return rejectWithValue({
        message: error.message || "Failed to delete team member",
      });
    }
  }
);
// Async thunk to add a team member to a team
export const addTeamMemberToTeam = createAsyncThunk(
  "addFunctions/addTeamMemberToTeam",
  async ({ teamId, memberId }, { rejectWithValue }) => {
    // Get token from memory (not localStorage)
    const token = await getAuthToken();
    if (!token) {
      return rejectWithValue({ message: "No token found for authentication" });
    }

    try {
      const response = await fetch(
        `${SERVER_DOMAIN}/api/teams/addmember/${teamId}`, // teamId in the URL
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({
            memberId: memberId, // memberId in the body
          }),
        }
      );

      const result = await response.json();

      if (!response.ok) {
        return rejectWithValue({
          message:
            result.message || "An error occurred while adding the team member.",
        });
      }

      // Return the updated team along with a success message
      return {
        message: result.message || "Team member added successfully",
        team: result.team || {}, // Assuming 'result.team' contains the updated team data
      };
    } catch (error) {
      return rejectWithValue({
        message: error.message || "Failed to add team member",
      });
    }
  }
);

// Slice definition
const addFunctionsSlice = createSlice({
  name: "addFunctions",
  initialState: {
    loading: false,
    error: null,
    successMessage: null,
    team: null, // Store the team data after adding the member
  },
  reducers: {
    clearSuccessMessage(state) {
      state.successMessage = null; // Clear the success message
      state.error = null; // Clear the error message
    },
  },
  extraReducers: (builder) => {
    builder
      // Handle add role actions
      .addCase(addRole.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(addRole.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;
      })
      .addCase(addRole.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.message;
      })
      // Handle add team member actions
      .addCase(addTeamMember.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(addTeamMember.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;
      })
      .addCase(addTeamMember.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.message;
      })
      // Handle create team actions
      .addCase(createTeam.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
      })
      .addCase(createTeam.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;
      })
      .addCase(createTeam.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.message;
      })
      // Handle delete team member actions
      .addCase(removeTeamMember.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
        state.team = null;
      })
      .addCase(removeTeamMember.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;
        state.team = action.payload.team; // Store the updated team data
      })
      .addCase(removeTeamMember.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.message;
        state.team = null;
      })
      // Handle add team member to team actions
      .addCase(addTeamMemberToTeam.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.successMessage = null;
        state.team = null;
      })
      .addCase(addTeamMemberToTeam.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload.message;
        state.team = action.payload.team; // Store the updated team data
      })
      .addCase(addTeamMemberToTeam.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload.message;
        state.team = null;
      });
  },
});

export const { clearSuccessMessage } = addFunctionsSlice.actions;

export default addFunctionsSlice.reducer;
